import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { cn } from "../lib/utils";
import { Sword, RotateCcw, RefreshCw, AlertTriangle, Bell, BellOff, Users, User, Heart, Flag, Play, EyeOff, ArrowUpDown, History, Clock, Target, Trophy, X } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import ReportUserModal from "./ReportUserModal";
import UserProfileModal from "./UserProfileModal";
import GuestProfileModal from "./GuestProfileModal";
import BountyPoster from "./BountyPoster";
import { useAuth } from "../context/AuthContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";
import { ScrollArea, ScrollBar } from "./ui/scroll-area";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// Cache for user data (avatar + supporter status) to avoid repeated API calls
const userDataCache = new Map();

// ChannelGrid: Tracker for boss channels
// Tiny avatar that auto-falls back to the profile-style User-icon placeholder
// when the supplied URL fails to load. Used in the boss-rankings modal.
const BossRankAvatar = ({ src, alt = "" }) => {
  const [broken, setBroken] = useState(false);
  if (!src || broken) {
    return (
      <div className="w-7 h-7 rounded bg-primary/20 border border-border/60 flex items-center justify-center shrink-0">
        <User className="w-4 h-4 text-primary" />
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      onError={() => setBroken(true)}
      className="w-7 h-7 rounded object-cover border border-border/60 shrink-0"
    />
  );
};

const ChannelGrid = ({
  boss,
  selectedLocationIndex,
  setSelectedLocationIndex,
  now,
  getBossImage,
  onMarkKill,
  onStartFight,
  onClearKill,
  onUndoKill,
  onKilledByOther,
  savingChannels,
  historyChannels,
  notificationsEnabled,
  toggleNotifications,
  fightNotificationsEnabled,
  toggleFightNotifications,
  hcNotificationsEnabled,
  toggleHcNotifications,
  onShowFightLog,
  fightLogCount,
  trackerType,
  selectedGuild,
  user,
  syncToGlobal,
  channelHistoryData = {},
  onAdminRevert,
  compactMode = false,
}) => {
  const location = boss.locations[selectedLocationIndex] || boss.locations[0];
  const navigate = useNavigate();
  const [killerData, setKillerData] = useState({});
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportingUser, setReportingUser] = useState(null);
  
  // User Profile Modal state
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [profileUsername, setProfileUsername] = useState(null);
  
  // Guest Profile Modal state (for admin viewing guest activity)
  const [guestProfileModalOpen, setGuestProfileModalOpen] = useState(false);
  const [guestProfileAlias, setGuestProfileAlias] = useState(null);
  
  // Check if user is admin
  const isAdmin = user?.is_admin || user?.is_junior_admin;
  
  // Sorting and filtering options
  const [sortBy, setSortBy] = useState(() => localStorage.getItem("sortBy") || "channel"); // "channel", "spawn_time", "overdue"
  const [hideKilled, setHideKilled] = useState(() => localStorage.getItem("hideKilled") === "true");
  
  // Track channels with active fights
  const [activeFights, setActiveFights] = useState(new Set());
  
  // Bounties for this boss
  const [bossBounties, setBossBounties] = useState([]);
  // Boss rankings modal
  const [bossRankingsOpen, setBossRankingsOpen] = useState(false);
  const [bossRankingsPeriod, setBossRankingsPeriod] = useState("last_7_days");
  const [bossRankingsData, setBossRankingsData] = useState([]);
  const [bossRankingsLoading, setBossRankingsLoading] = useState(false);
  const { token } = useAuth();
  
  // Parchment background URL
  const PARCHMENT_BG = "https://customer-assets.emergentagent.com/job_bounty-hub-5/artifacts/9qj2hqka_Gemini_Generated_Image_w0kodsw0kodsw0ko.png";
  
  // Boss image mapping for bounties
  const BOSS_IMAGE_MAPPING = {
    "Mano": "https://legends.ml/static/images/lib/monster/2220000.png",
    "Stumpy": "https://legends.ml/static/images/lib/monster/3220000.png",
    "Deo": "https://legends.ml/static/images/lib/monster/3220001.png",
    "King Clang": "https://legends.ml/static/images/lib/monster/5220000.png",
    "Seruf": "https://legends.ml/static/images/lib/monster/4220001.png",
    "Faust": "https://legends.ml/static/images/lib/monster/5220002.png",
    "Giant Centipede": "https://legends.ml/static/images/lib/monster/9500177.png",
    "Timer": "https://legends.ml/static/images/lib/monster/5220003.png",
    "Mushmom": "https://legends.ml/static/images/lib/monster/6130101.png",
    "Dyle": "https://legends.ml/static/images/lib/monster/6220000.png",
    "Zombie Mushmom": "https://legends.ml/static/images/lib/monster/6300005.png",
    "Zeno": "https://legends.ml/static/images/lib/monster/6220001.png",
    "Nine-Tailed Fox": "https://legends.ml/static/images/lib/monster/7220001.png",
    "Tae Roon": "https://legends.ml/static/images/lib/monster/7220000.png",
    "King Sage Cat": "https://legends.ml/static/images/lib/monster/7220002.png",
    "Jr. Balrog": "https://legends.ml/static/images/lib/monster/8130100.png",
    "Eliza": "https://legends.ml/static/images/lib/monster/8220000.png",
    "Snack Bar": "https://legends.ml/static/images/lib/monster/9410015.png",
    "Chimera": "https://legends.ml/static/images/lib/monster/8220002.png",
    "Blue Mushmom": "https://legends.ml/static/images/lib/monster/9400205.png",
    "Snowman": "https://legends.ml/static/images/lib/monster/8220001.png",
    "Headless Horseman": "https://legends.ml/static/images/lib/monster/9400549.png",
    "Manon": "https://legends.ml/static/images/lib/monster/9500174.png",
    "Griffey": "https://legends.ml/static/images/lib/monster/8180001.png",
    "Pianus (16h)": "https://legends.ml/static/images/lib/monster/8510000.png",
    "Pianus (24h)": "https://legends.ml/static/images/lib/monster/8520000.png",
    "BigFoot": "https://legends.ml/static/images/lib/monster/9400575.png",
    "Black Crow": "https://legends.ml/static/images/lib/monster/9400014.png",
    "Leviathan": "https://legends.ml/static/images/lib/monster/8220003.png",
    "Kacchuu Musha": "https://legends.ml/static/images/lib/monster/9400405.png",
    "Dodo": "https://legends.ml/static/images/lib/monster/8220004.png",
    "Anego / Female Boss": "https://legends.ml/static/images/lib/monster/9400121.png",
    "Lilynouch": "https://legends.ml/static/images/lib/monster/8220005.png",
    "Lyka": "https://legends.ml/static/images/lib/monster/8220006.png"
  };
  
  const getBountyBossImage = (bossName) => {
    const baseName = bossName.split(" - ")[0];
    return BOSS_IMAGE_MAPPING[baseName] || "https://legends.ml/static/images/lib/monster/6130101.png";
  };
  
  // Fetch bounties for this boss
  useEffect(() => {
    const fetchBounties = async () => {
      try {
        // Match bounties that start with the boss name (to catch location variants)
        const res = await axios.get(`${API_URL}/api/bounties/for-boss/${encodeURIComponent(boss.name)}`);
        setBossBounties(res.data.bounties || []);
      } catch (err) {
        // Silently fail - bounties are optional
        console.log("No bounties found for", boss.name);
      }
    };
    
    if (boss?.name) {
      fetchBounties();
      // Refresh every 30 seconds
      const interval = setInterval(fetchBounties, 30000);
      return () => clearInterval(interval);
    }
  }, [boss?.name]);
  
  // Boss rankings: fetch when modal opens or period changes
  useEffect(() => {
    if (!bossRankingsOpen) return;
    let cancelled = false;
    const fetchRankings = async () => {
      setBossRankingsLoading(true);
      try {
        const r = await axios.get(
          `${API_URL}/api/rankings/kills?period=${bossRankingsPeriod}&boss_name=${encodeURIComponent(boss.name)}&limit=50`
        );
        if (!cancelled) setBossRankingsData(r.data?.leaderboard || []);
      } catch (e) {
        if (!cancelled) setBossRankingsData([]);
      } finally {
        if (!cancelled) setBossRankingsLoading(false);
      }
    };
    fetchRankings();
    return () => {
      cancelled = true;
    };
  }, [bossRankingsOpen, bossRankingsPeriod, boss.name]);

  // Accept bounty handler
  const handleAcceptBounty = async (bountyId) => {
    if (!user) {
      toast.error("Please log in to accept a bounty");
      return;
    }
    
    try {
      await axios.put(`${API_URL}/api/bounties/${bountyId}/assign`);
      toast.success("Bounty accepted! Contact the client in-game.");
      // Refresh bounties
      const res = await axios.get(`${API_URL}/api/bounties/for-boss/${encodeURIComponent(boss.name)}`);
      setBossBounties(res.data.bounties || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to accept bounty");
    }
  };

  // Local state for 'Last Checked' timers
  const [lastCheckedTimes, setLastCheckedTimes] = useState(() => {
    try {
      const saved = localStorage.getItem("lastCheckedTimes");
      if (saved) {
        const parsed = JSON.parse(saved);
        // Clear expired on load (older than 1h)
        const nowMs = Date.now();
        const valid = {};
        for (const [key, time] of Object.entries(parsed)) {
          if (nowMs - time < 60 * 60 * 1000) {
            valid[key] = time;
          }
        }
        return valid;
      }
    } catch(e) {}
    return {};
  });

  // Save lastCheckedTimes to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("lastCheckedTimes", JSON.stringify(lastCheckedTimes));
  }, [lastCheckedTimes]);

  // Clean up expired timers on every tick (now)
  useEffect(() => {
    const expiredKeys = Object.entries(lastCheckedTimes)
      .filter(([_, time]) => now - time >= 60 * 60 * 1000)
      .map(([key]) => key);

    if (expiredKeys.length > 0) {
      setLastCheckedTimes(prev => {
        const next = { ...prev };
        expiredKeys.forEach(k => delete next[k]);
        return next;
      });
    }
  }, [now, lastCheckedTimes]);

  const handleLastChecked = (channelIndex) => {
    const key = `${location.backendName}-${channelIndex}`;
    setLastCheckedTimes(prev => ({
      ...prev,
      [key]: Date.now()
    }));
  };

  const getLastCheckedData = (channelIndex) => {
    const key = `${location.backendName}-${channelIndex}`;
    const time = lastCheckedTimes[key];
    if (!time) return null;

    const elapsedMs = now - time;
    if (elapsedMs < 0 || elapsedMs >= 60 * 60 * 1000) return null;

    const minutes = Math.floor(elapsedMs / 60000);
    const seconds = Math.floor((elapsedMs % 60000) / 1000);
    const display = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;

    let colorClass = "text-emerald-500 font-bold";
    if (minutes >= 45) {
      colorClass = "text-red-500 font-bold";
    } else if (minutes >= 30) {
      colorClass = "text-amber-500 font-bold";
    } else {
      colorClass = "text-emerald-500 font-bold";
    }

    return { display, colorClass };
  };


  // Open report modal for a user
  const handleReportUser = (username, userId) => {
    if (!user) {
      toast.error("You must be logged in to report users");
      return;
    }
    setReportingUser({ username, id: userId || username }); // Use username as ID fallback
    setReportModalOpen(true);
  };

  // Fetch data for killers (avatar + supporter status)
  useEffect(() => {
    const fetchKillerData = async () => {
      if (!location || !location.channels || !Array.isArray(location.channels)) return;

      const killers = location.channels
        .filter((ch) => ch && ch.killer)
        .map((ch) => ch.killer)
        .filter((killer, idx, arr) => arr.indexOf(killer) === idx); // unique

      const newData = {};
      for (const killer of killers) {
        if (userDataCache.has(killer)) {
          newData[killer] = userDataCache.get(killer);
        } else {
          try {
            const response = await axios.get(`${API_URL}/api/users/${encodeURIComponent(killer)}/avatar`);
            const data = {
              avatar: response.data.avatar || null,
              is_kofi_supporter: response.data.is_kofi_supporter || false
            };
            userDataCache.set(killer, data);
            newData[killer] = data;
          } catch {
            const data = { avatar: null, is_kofi_supporter: false };
            userDataCache.set(killer, data);
            newData[killer] = data;
          }
        }
      }
      setKillerData(newData);
    };

    fetchKillerData();
  }, [location]);

  // Format time display
  const formatTime = (ms) => {
    let seconds = Math.floor(ms / 1000);
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return [
      h.toString().padStart(2, "0"),
      m.toString().padStart(2, "0"),
      s.toString().padStart(2, "0"),
    ].join(":");
  };

  // Send Discord notification for guild
  const sendDiscordNotification = async (channelIndex, status) => {
    if (!selectedGuild?.discord_webhook || trackerType !== "guild") return;
    
    try {
      await axios.post(`${API_URL}/api/notify/discord`, {
        guild_id: selectedGuild.id,
        boss_name: boss.name,
        channel: channelIndex,
        status: status,
      });
      toast.success("Discord notification sent!");
    } catch (error) {
      // Silent fail for notifications
    }
  };

  // Calculate channel data
  const calculateChannelData = (channel, baseTimeMinutes, channelIndex) => {
    const key = `${location.backendName}-${channelIndex}`;
    const isSaving = savingChannels[key] || false;
    const hasHistory = !!historyChannels[key];

    if (!channel || channel.time === 0) {
      return {
        state: "unknown",
        stateText: "NO DATA",
        displayTime: "--:--:--",
        subText: "Awaiting first kill record",
        killer: "",
        isSaving,
        hasHistory,
        sortValue: Infinity, // Put unknown at end when sorting
        channelIndex,
      };
    }

    const killTime = channel.time;
    const killerName = channel.killer || "Anonymous";
    const baseMs = baseTimeMinutes * 60 * 1000;
    const minSpawnMs = killTime + baseMs * 0.8;
    const maxSpawnMs = killTime + baseMs * 1.2;
    
    // Check if overdue by more than 2 hours (auto-clear)
    const twoHoursMs = 2 * 60 * 60 * 1000;
    if (now > maxSpawnMs + twoHoursMs) {
      return {
        state: "unknown",
        stateText: "EXPIRED",
        displayTime: "--:--:--",
        subText: "Timer expired (2h overdue)",
        killer: "",
        isSaving,
        hasHistory,
        sortValue: Infinity,
        channelIndex,
        isExpired: true,
      };
    }

    if (now < minSpawnMs) {
      const diff = minSpawnMs - now;
      return {
        state: "dead",
        stateText: "DEAD",
        displayTime: formatTime(diff),
        subText: "Until window opens",
        killer: killerName,
        isSaving,
        hasHistory,
        sortValue: minSpawnMs,
        channelIndex,
      };
    } else if (now >= minSpawnMs && now < maxSpawnMs) {
      const diff = maxSpawnMs - now;
      return {
        state: "window",
        stateText: "SPAWN WINDOW",
        displayTime: formatTime(diff),
        subText: "Window closes in",
        killer: killerName,
        isSaving,
        hasHistory,
        sortValue: minSpawnMs,
        channelIndex,
      };
    } else {
      const diff = now - maxSpawnMs;
      return {
        state: "up",
        stateText: "UP",
        displayTime: formatTime(diff),
        subText: "Overdue by",
        killer: killerName,
        isSaving,
        hasHistory,
        sortValue: minSpawnMs,
        channelIndex,
      };
    }
  };

  // Calculate all channel data and apply sorting/filtering
  const processedChannels = useMemo(() => {
    if (!location || !location.channels || !Array.isArray(location.channels)) return [];
    let channels = location.channels.map((channel, idx) => ({
      channel,
      data: calculateChannelData(channel, boss.baseTime, idx),
      originalIndex: idx,
    }));

    // Filter out only dead channels if hideKilled is enabled (keep unknown and expired)
    if (hideKilled) {
      channels = channels.filter(({ data }) => data.state !== "dead");
    }

    // Sort channels
    if (sortBy === "spawn_time") {
      channels.sort((a, b) => a.data.sortValue - b.data.sortValue);
    } else if (sortBy === "overdue") {
      // Sort by overdue first (most overdue), then window, then dead
      channels.sort((a, b) => {
        const stateOrder = { up: 0, window: 1, dead: 2, unknown: 3 };
        const aOrder = stateOrder[a.data.state] ?? 3;
        const bOrder = stateOrder[b.data.state] ?? 3;
        if (aOrder !== bOrder) return aOrder - bOrder;
        return a.data.sortValue - b.data.sortValue;
      });
    }
    // Default "channel" sorting keeps original order

    return channels;
  }, [location.channels, boss.baseTime, now, sortBy, hideKilled]); // eslint-disable-line react-hooks/exhaustive-deps

  const getStateColor = (state) => {
    switch (state) {
      case "up":
        return "text-emerald-400";
      case "window":
        return "text-amber-400";
      case "dead":
        return "text-red-400";
      default:
        return "text-muted-foreground";
    }
  };

  const getStatusBorderClass = (state) => {
    switch (state) {
      case "up":
        return "status-border-up";
      case "window":
        return "status-border-window";
      case "dead":
        return "status-border-dead";
      default:
        return "status-border-unknown";
    }
  };

  return (
    <div className="p-4 lg:p-6" data-testid="channel-grid">
      {/* Boss Header */}
      <div className="mb-6 pb-4 border-b border-border">
        <div className="flex items-start gap-6">
          {/* Boss Image */}
          <div className="hidden sm:flex w-24 h-24 bg-card rounded-xl items-center justify-center p-2 border border-border flex-shrink-0">
            <img
              src={getBossImage(boss.imageKey)}
              alt={boss.name}
              className="max-w-full max-h-full object-contain"
            />
          </div>

          {/* Boss Info */}
          <div className="flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl lg:text-3xl font-bold text-primary" data-testid="boss-name">
                {boss.name}
              </h1>
              {trackerType === "guild" && selectedGuild && (
                <span className="px-2 py-1 bg-primary/20 rounded text-xs font-medium flex items-center gap-1">
                  <Users className="w-3 h-3" />
                  {selectedGuild.name}
                </span>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setBossRankingsOpen(true)}
                className="ml-auto gap-1.5 text-yellow-300 border-yellow-500/50 hover:bg-yellow-500/10 h-8"
                title={`Top hunters of ${boss.name}`}
                data-testid="boss-rankings-btn"
              >
                <Trophy className="w-4 h-4" />
                <span className="hidden sm:inline">Top hunters</span>
              </Button>
            </div>
            <p className="text-muted-foreground mt-1">
              Base: {boss.baseTime / 60}h (Window: {(boss.baseTime * 0.8) / 60}h -{" "}
              {(boss.baseTime * 1.2) / 60}h)
            </p>

            {/* Manon Warning */}
            {boss.name === "Manon" && (
              <div className="mt-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-200">
                  <strong>Note:</strong> This monster shouldn't be hunted unless
                  necessary, as it is a prerequisite for the Horntail quest.
                </p>
              </div>
            )}
            
            {/* Active Bounties for this Boss */}
            {bossBounties.length > 0 && (
              <div className="mt-4">
                <div className="flex items-center gap-2 mb-2">
                  <Target className="w-4 h-4 text-amber-400" />
                  <span className="text-sm font-semibold text-amber-400">
                    Bounties ({bossBounties.length})
                  </span>
                </div>
                <ScrollArea className="w-full">
                  <div className="flex gap-3 pb-2">
                    {bossBounties.map((bounty) => (
                      <div key={bounty.id} className="flex-shrink-0 w-72 cursor-pointer" onClick={() => navigate(`/hunters-chance/bounty/${bounty.id}`)} data-testid={`compact-bounty-${bounty.id}`}>
                        <BountyPoster
                          bounty={bounty}
                          isHunter={user && bounty.client_id !== user.id}
                          onAccept={() => handleAcceptBounty(bounty.id)}
                          onViewDetails={() => navigate(`/hunters-chance/bounty/${bounty.id}`)}
                          parchmentBg={PARCHMENT_BG}
                          bossImage={getBountyBossImage(bounty.boss_name)}
                          compact={true}
                        />
                      </div>
                    ))}
                  </div>
                  <ScrollBar orientation="horizontal" />
                </ScrollArea>
              </div>
            )}

            {/* Controls Row */}
            <div className="mt-4 flex flex-wrap items-center gap-4">
              {/* Location Tabs */}
              {boss.locations.length > 1 && (
                <div className="flex flex-wrap gap-2" data-testid="location-tabs">
                  {boss.locations.map((loc, idx) => (
                    <Button
                      key={idx}
                      variant={selectedLocationIndex === idx ? "default" : "secondary"}
                      size="sm"
                      onClick={() => setSelectedLocationIndex(idx)}
                      data-testid={`location-tab-${idx}`}
                    >
                      {loc.locationName}
                    </Button>
                  ))}
                </div>
              )}

              {/* Sorting Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-2">
                    <ArrowUpDown className="w-4 h-4" />
                    Sort: {sortBy === "channel" ? "Channel" : sortBy === "spawn_time" ? "Spawn Time" : "Overdue"}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem onClick={() => { setSortBy("channel"); localStorage.setItem("sortBy", "channel"); }}>
                    By Channel Number
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => { setSortBy("spawn_time"); localStorage.setItem("sortBy", "spawn_time"); }}>
                    By Spawn Time (Soonest)
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => { setSortBy("overdue"); localStorage.setItem("sortBy", "overdue"); }}>
                    By Overdue (Most First)
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Hide Killed Toggle */}
              <Button
                variant={hideKilled ? "default" : "outline"}
                size="sm"
                onClick={() => {
                  const newValue = !hideKilled;
                  setHideKilled(newValue);
                  localStorage.setItem("hideKilled", newValue.toString());
                }}
                className="gap-2"
                data-testid="hide-killed-toggle"
              >
                <EyeOff className="w-4 h-4" />
                {hideKilled ? "Show All" : "Hide Dead"}
              </Button>

              {/* Notification Toggle */}
              <Button
                variant="outline"
                size="sm"
                onClick={toggleNotifications}
                className="gap-2 ml-auto hidden lg:flex"
                data-testid="notifications-toggle"
              >
                {notificationsEnabled ? (
                  <>
                    <Bell className="w-4 h-4 text-primary" />
                  Spawn notifications
                  </>
                ) : (
                  <>
                    <BellOff className="w-4 h-4" />
                  Spawn notifications
                  </>
                )}
              </Button>

              {/* Fight Notifications Toggle with Tooltip */}
              {user && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant={fightNotificationsEnabled ? "default" : "outline"}
                        size="sm"
                        onClick={toggleFightNotifications}
                        className="gap-2 hidden lg:flex"
                        data-testid="fight-notifications-toggle"
                      >
                        <Sword className={cn("w-4 h-4", fightNotificationsEnabled && "text-white")} />
                        {fightNotificationsEnabled ? "Fight Alerts On" : "Fight Alerts Off"}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-[250px]">
                      <p className="text-sm">
                        {fightNotificationsEnabled 
                          ? "You will see and send fight notifications to other synced players."
                          : "Fight alerts are OFF. You won't see or send notifications to others."}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}

              {/* Fight Log Button */}
              {user && onShowFightLog && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onShowFightLog}
                  className="gap-2 hidden lg:flex"
                  data-testid="fight-log-button"
                >
                  <History className="w-4 h-4" />
                  Log
                  {fightLogCount > 0 && (
                    <span className="bg-muted px-1.5 rounded text-xs">{fightLogCount}</span>
                  )}
                </Button>
              )}

              {/* Hunter's Chance Alerts Toggle */}
              {user && toggleHcNotifications && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant={hcNotificationsEnabled ? "default" : "outline"}
                        size="sm"
                        onClick={toggleHcNotifications}
                        className="gap-2 hidden lg:flex"
                        style={hcNotificationsEnabled ? { background: "linear-gradient(135deg, #8B4513 0%, #5D3A1A 100%)", border: "1px solid #FFD700" } : {}}
                        data-testid="hc-notifications-toggle"
                      >
                        <Target className={cn("w-4 h-4", hcNotificationsEnabled && "text-amber-200")} />
                        {hcNotificationsEnabled ? "HC Alerts On" : "HC Alerts Off"}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-[250px]">
                      <p className="text-sm">
                        {hcNotificationsEnabled 
                          ? "Hunter's Chance alerts ON - You'll see bounty & expedition notifications"
                          : "Hunter's Chance alerts OFF - No bounty/expedition notifications"}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}

              {/* Discord Notify Button (Guild only) */}
              {trackerType === "guild" && selectedGuild?.discord_webhook && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const windowChannels = location.channels
                      .map((ch, idx) => ({ ch, idx }))
                      .filter(({ ch }) => {
                        if (!ch || ch.time === 0) return false;
                        const baseMs = boss.baseTime * 60 * 1000;
                        const minSpawnMs = ch.time + baseMs * 0.8;
                        const maxSpawnMs = ch.time + baseMs * 1.2;
                        return now >= minSpawnMs && now < maxSpawnMs;
                      });
                    
                    if (windowChannels.length > 0) {
                      windowChannels.forEach(({ idx }) => {
                        sendDiscordNotification(idx, "window");
                      });
                    } else {
                      toast.info("No channels in spawn window");
                    }
                  }}
                  className="gap-2"
                  data-testid="discord-notify-button"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
                  </svg>
                  Notify Discord
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Channel Cards Grid */}
      <div
        className={cn(
          "grid gap-4",
          compactMode
            ? "grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2"
            : "grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4"
        )}
        data-testid="channels-container"
        data-compact={compactMode ? "true" : "false"}
      >
        {processedChannels.map(({ data, originalIndex: idx }) => {
          const hasFightStarted = activeFights.has(idx);
          const lastChecked = getLastCheckedData(idx);

          return (
            <Card
              key={idx}
              className={cn(
                "flex flex-col transition-all duration-300",
                compactMode ? "p-2" : "p-4",
                getStatusBorderClass(data.state),
                data.state === "window" && "animate-pulse-window bg-amber-500/5",
                data.state === "up" && "bg-emerald-500/5",
                hasFightStarted && "animate-pulse-fighting font-bold"
              )}
              data-testid={`channel-card-${idx}`}
            >
              {/* Channel Header */}
              <div className={cn("flex justify-between items-center", compactMode ? "mb-1.5" : "mb-3")}>
                <h3 className={cn(
                  "font-bold font-mono",
                  compactMode ? "text-xs" : "",
                  hasFightStarted && "text-green-500"
                )}>CH {idx + 1}</h3>
                <span
                  className={cn(
                    "uppercase font-bold tracking-wider",
                    compactMode ? "text-[9px]" : "text-xs",
                    getStateColor(data.state)
                  )}
                >
                  {data.stateText}
                </span>
              </div>

              {/* Timer Display */}
              <div className={cn(
                "text-center flex-1 flex flex-col justify-center",
                compactMode ? "py-1" : "py-3"
              )}>
                <div className={cn(
                  "font-bold font-mono",
                  compactMode ? "text-base" : "text-2xl lg:text-3xl"
                )} data-testid={`channel-${idx}-timer`}>
                  {data.displayTime}
                </div>
                <div className={cn(
                  "text-muted-foreground",
                  compactMode ? "text-[9px] mt-0" : "text-xs mt-1"
                )}>{data.subText}</div>

                {/* Killer Name with Avatar */}
                {data.state !== "unknown" && data.killer && (
                  <div className={cn(
                    "mt-3 inline-flex items-center gap-2 px-2 py-1 rounded text-xs font-medium group relative",
                    killerData[data.killer]?.is_kofi_supporter 
                      ? "bg-amber-500/20 text-amber-400" 
                      : "bg-background text-primary"
                  )}>
                    {killerData[data.killer]?.avatar ? (
                      <img 
                        src={killerData[data.killer].avatar} 
                        alt={data.killer}
                        className="w-5 h-5 rounded-full object-cover"
                      />
                    ) : (
                      <div className={cn(
                        "w-5 h-5 rounded-full flex items-center justify-center",
                        killerData[data.killer]?.is_kofi_supporter 
                          ? "bg-amber-500/30" 
                          : "bg-primary/20"
                      )}>
                        <User className={cn(
                          "w-3 h-3",
                          killerData[data.killer]?.is_kofi_supporter 
                            ? "text-amber-400" 
                            : "text-primary"
                        )} />
                      </div>
                    )}
                    {killerData[data.killer]?.is_kofi_supporter && (
                      <Heart className="w-3 h-3 text-amber-400 fill-amber-400" />
                    )}
                    {/* Clickable Killer Name */}
                    {data.killer !== "Anonymous" ? (
                      data.killer.startsWith("Anonymous_") ? (
                        // Guest user with ore-based alias - clickable for admins
                        isAdmin ? (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setGuestProfileAlias(data.killer);
                              setGuestProfileModalOpen(true);
                            }}
                            className="hover:underline text-amber-400"
                            title={`View ${data.killer}'s activity (Admin)`}
                          >
                            Killer: {data.killer}
                          </button>
                        ) : (
                          <span className="text-amber-400">Killer: {data.killer}</span>
                        )
                      ) : (
                        // Registered user - always clickable
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setProfileUsername(data.killer);
                            setProfileModalOpen(true);
                          }}
                          className="hover:underline"
                          title={`View ${data.killer}'s profile`}
                        >
                          Killer: {data.killer}
                        </button>
                      )
                    ) : (
                      <span>Killer: {data.killer}</span>
                    )}
                    {/* Report button - only show if logged in and not own kill and not a guest */}
                    {user && data.killer !== user.username && data.killer !== "Anonymous" && !data.killer.startsWith("Anonymous_") && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleReportUser(data.killer);
                        }}
                        className="ml-1 p-1 rounded hover:bg-destructive/20 text-muted-foreground hover:text-destructive transition-colors opacity-0 group-hover:opacity-100"
                        title="Report user"
                      >
                        <Flag className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col gap-2 mt-3">
                {/* Start Fight Button - Show for all states when user is logged in and synced */}
                {user && (trackerType === "global" || syncToGlobal) && !hasFightStarted && (
                  <Button
                    onClick={() => {
                      setActiveFights(prev => new Set([...prev, idx]));
                      onStartFight(idx);
                      // Auto-clear fight status after 5 minutes
                      setTimeout(() => {
                        setActiveFights(prev => {
                          const newSet = new Set(prev);
                          newSet.delete(idx);
                          return newSet;
                        });
                      }, 5 * 60 * 1000);
                    }}
                    variant="outline"
                    className="w-full text-xs border-green-500 text-green-500 hover:bg-green-500/10"
                    data-testid={`start-fight-button-${idx}`}
                  >
                    <Play className="w-4 h-4 mr-1 fill-green-500" />
                    Start Fight
                  </Button>
                )}
                
                {/* Fight in progress indicator */}
                {hasFightStarted && (
                  <div className="flex flex-col gap-1">
                    <div className="text-center text-xs text-red-500 font-bold py-2 bg-red-500/10 rounded-md border border-red-500/30 flex items-center justify-center animate-pulse-fighting">
                      ⚔️ Fight in progress...
                    </div>
                    <Button
                      onClick={() => {
                        setActiveFights(prev => {
                          const newSet = new Set(prev);
                          newSet.delete(idx);
                          return newSet;
                        });
                      }}
                      variant="outline"
                      className="w-full text-xs border-red-500 text-red-500 hover:bg-red-500/10"
                      data-testid={`cancel-fight-button-${idx}`}
                    >
                      <RotateCcw className="w-4 h-4 mr-1" />
                      Cancel Fight
                    </Button>
                  </div>
                )}
                

                {/* Last Checked Button */}
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        onClick={() => handleLastChecked(idx)}
                        variant="outline"
                        className="w-full text-xs flex justify-between items-center px-3"
                        data-testid={`last-checked-button-${idx}`}
                      >
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          <span>Last Checked</span>
                        </div>
                        {lastChecked && (
                          <span className={lastChecked.colorClass}>
                            {lastChecked.display}
                          </span>
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-[250px]">
                      <p className="text-sm">
                        Checked channel and Boss was not there? See when you last time checked this channel.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                <div className="flex gap-2">

                  <Button
                    onClick={() => {
                      onMarkKill(idx);
                      // Clear fight status when killed
                      setActiveFights(prev => {
                        const newSet = new Set(prev);
                        newSet.delete(idx);
                        return newSet;
                      });
                    }}
                    disabled={data.isSaving}
                    variant="destructive"
                    className="flex-1 text-xs"
                    data-testid={`mark-killed-button-${idx}`}
                  >
                    {data.isSaving ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Sword className="w-4 h-4 mr-1" />
                        Kill
                      </>
                    )}
                  </Button>

                  {data.hasHistory && (
                    <Button
                      onClick={() => onUndoKill(idx)}
                      disabled={data.isSaving}
                      variant="secondary"
                      size="icon"
                      className="flex-shrink-0"
                      title="Undo last action"
                      data-testid={`undo-button-${idx}`}
                    >
                      <RotateCcw className="w-4 h-4" />
                    </Button>
                  )}

                  {/* Admin Revert Dropdown - always visible for admins when history exists */}
                  {user && (user.is_admin || user.is_junior_admin) && channelHistoryData[String(idx)] && channelHistoryData[String(idx)].length > 0 && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          disabled={data.isSaving}
                          variant="outline"
                          size="icon"
                          className="flex-shrink-0 border-amber-500/50 text-amber-500 hover:bg-amber-500/10"
                          title="Admin Revert History"
                          data-testid={`admin-revert-button-${idx}`}
                        >
                          <History className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-64">
                        <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">Revert History (CH {idx + 1})</div>
                        {channelHistoryData[String(idx)].map((entry) => (
                          <DropdownMenuItem
                            key={entry.id}
                            onClick={() => onAdminRevert(entry.id, idx)}
                            className="flex flex-col items-start gap-0.5 cursor-pointer"
                            data-testid={`admin-revert-entry-${entry.id}`}
                          >
                            <span className="text-xs font-medium">
                              {entry.action === "kill" ? "Kill" : entry.action === "clear" ? "Clear" : "Killed by Other"} by {entry.changed_by_username}
                            </span>
                            <span className="text-[10px] text-muted-foreground">
                              Revert to: {entry.previous_state.killer || "Empty"} {entry.previous_state.time ? `(${new Date(entry.previous_state.time).toLocaleTimeString()})` : "(No timer)"}
                            </span>
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}

                  {/* Clear button only shown when in window or up state */}
                  {(data.state === "window" || data.state === "up") && (
                    <Button
                      onClick={() => onClearKill(idx)}
                      disabled={data.isSaving}
                      variant="outline"
                      size="icon"
                      className="flex-shrink-0"
                      title="Clear timer (visited but no spawn)"
                      data-testid={`clear-button-${idx}`}
                    >
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                  )}
                </div>

                {/* Killed by Other button with tooltip */}
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        onClick={() => {
                          onKilledByOther(idx);
                          setActiveFights(prev => {
                            const newSet = new Set(prev);
                            newSet.delete(idx);
                            return newSet;
                          });
                        }}
                        disabled={data.isSaving}
                        variant="ghost"
                        className="w-full text-xs text-muted-foreground hover:text-foreground"
                        data-testid={`killed-by-other-button-${idx}`}
                      >
                        <Users className="w-3 h-3 mr-1" />
                        Killed by Other
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-[220px]">
                      <p className="text-sm">
                        Use this when you see someone else fighting/killing the boss. 
                        <span className="text-muted-foreground block mt-1">Does not count in your statistics.</span>
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Empty state when all channels are hidden */}
      {processedChannels.length === 0 && hideKilled && (
        <div className="text-center py-12 text-muted-foreground">
          <EyeOff className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>All channels are currently dead or unknown.</p>
          <Button 
            variant="link" 
            onClick={() => setHideKilled(false)}
            className="mt-2"
          >
            Show all channels
          </Button>
        </div>
      )}

      {/* Report User Modal */}
      {reportingUser && (
        <ReportUserModal
          open={reportModalOpen}
          onOpenChange={setReportModalOpen}
          reportedUser={reportingUser}
        />
      )}

      {/* User Profile Modal */}
      <UserProfileModal
        open={profileModalOpen}
        onOpenChange={setProfileModalOpen}
        username={profileUsername}
      />
      
      {/* Guest Profile Modal (Admin only) */}
      <GuestProfileModal
        isOpen={guestProfileModalOpen}
        onClose={() => setGuestProfileModalOpen(false)}
        guestAlias={guestProfileAlias}
      />

      {/* Boss Rankings Modal — fancy parchment scroll */}
      {bossRankingsOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm"
          onClick={() => setBossRankingsOpen(false)}
          data-testid="boss-rankings-modal"
        >
          <div
            className="relative w-full max-w-md max-h-[88vh] overflow-hidden rounded-md border-[3px] flex flex-col shadow-2xl"
            style={{
              borderColor: "#c9a04a",
              background: "radial-gradient(ellipse at top, #fff8e6 0%, #f4e7c2 55%, #e3d29a 100%)",
              boxShadow: "inset 0 0 60px rgba(160, 110, 40, 0.18), 0 14px 40px rgba(0,0,0,0.4)",
              color: "#3a2509",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Engraved boss image watermark */}
            <img
              src={getBossImage(boss.imageKey)}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 w-full h-full object-contain pointer-events-none select-none"
              style={{
                opacity: 0.09,
                filter: "saturate(0.4) sepia(0.5)",
                imageRendering: "pixelated",
                transform: "scale(0.85)",
              }}
            />
            <div className="relative z-[1] px-4 py-3 border-b-2 border-amber-700/40 flex items-center gap-2">
              <Trophy className="w-5 h-5 text-amber-700" />
              <div className="flex-1 min-w-0">
                <h3
                  className="font-extrabold text-lg leading-tight truncate"
                  style={{ fontFamily: "'Cinzel', Georgia, serif", color: "#3a2509" }}
                >
                  Champions of {boss.name}
                </h3>
                <p className="text-[11px] text-amber-900/70 italic">
                  Top hunters who claimed this boss
                </p>
              </div>
              <button
                onClick={() => setBossRankingsOpen(false)}
                className="text-amber-800 hover:text-amber-950 p-1 rounded hover:bg-amber-300/30"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="relative z-[1] px-4 pt-3 pb-2 flex flex-wrap gap-1.5 border-b border-amber-700/30">
              {[
                { id: "today", label: "Today" },
                { id: "last_7_days", label: "7d" },
                { id: "last_30_days", label: "30d" },
                { id: "this_month", label: "Month" },
                { id: "all_time", label: "All-time" },
              ].map((p) => (
                <button
                  key={p.id}
                  onClick={() => setBossRankingsPeriod(p.id)}
                  className={cn(
                    "px-2.5 py-1 rounded-full text-xs font-extrabold border-2 transition",
                    bossRankingsPeriod === p.id
                      ? "bg-amber-500 text-amber-950 border-amber-700"
                      : "bg-amber-100/40 text-amber-900 border-amber-700/40 hover:bg-amber-300/40"
                  )}
                  data-testid={`boss-rank-period-${p.id}`}
                >
                  {p.label}
                </button>
              ))}
              <button
                onClick={() => {
                  setBossRankingsOpen(false);
                  navigate(`/rankings?tab=by-boss&season=week_0&boss=${encodeURIComponent(boss.name)}`);
                }}
                className="ml-auto text-xs font-extrabold text-amber-800 hover:text-amber-950 underline-offset-2 hover:underline"
                data-testid="boss-rank-fullpage-btn"
              >
                Open codex →
              </button>
            </div>
            <div className="relative z-[1] flex-1 overflow-y-auto p-3">
              {bossRankingsLoading ? (
                <div className="flex items-center justify-center py-10 text-amber-900/70">
                  <RefreshCw className="w-5 h-5 animate-spin mr-2" /> Loading…
                </div>
              ) : bossRankingsData.length === 0 ? (
                <div className="text-center py-10 text-amber-900/70 italic text-sm">
                  No deeds recorded against this boss in the selected period.
                </div>
              ) : (
                <div className="space-y-0.5">
                  {bossRankingsData.map((row) => {
                    const rankColor =
                      row.rank === 1
                        ? "#7a4a10"
                        : row.rank === 2
                        ? "#8a6a2a"
                        : row.rank === 3
                        ? "#9a7a3a"
                        : "#a18244";
                    const crown =
                      row.rank === 1 ? "👑" : row.rank === 2 ? "🥈" : row.rank === 3 ? "🥉" : null;
                    return (
                      <div
                        key={row.user_id}
                        className={cn(
                          "grid grid-cols-[40px_28px_1fr_auto] items-center gap-2 px-2 py-2 rounded transition hover:bg-amber-300/25",
                          row.rank === 1 && "bg-amber-300/30 border-2 border-amber-600/60"
                        )}
                        style={{ borderBottom: "1px dashed rgba(150,110,30,0.3)" }}
                        data-testid={`boss-rank-row-${row.rank}`}
                      >
                        <span className="font-mono font-extrabold text-base text-center" style={{ color: rankColor }}>
                          {crown ? <span className="text-lg">{crown}</span> : String(row.rank).padStart(2, "0")}
                        </span>
                        {row.avatar ? (
                          <BossRankAvatar src={row.avatar} alt={row.username} />
                        ) : (
                          <BossRankAvatar src={null} alt={row.username} />
                        )}
                        <div className="min-w-0 flex items-center gap-1">
                          <span
                            className="font-bold text-sm truncate"
                            style={{ color: "#3a2509", fontFamily: "'Cinzel', Georgia, serif" }}
                          >
                            {row.username}
                          </span>
                          {row.is_kofi_supporter && (
                            <Heart className="w-3 h-3 text-rose-500 fill-rose-500" />
                          )}
                        </div>
                        <div className="text-right shrink-0">
                          <div className="font-extrabold font-mono tabular-nums" style={{ color: "#3a2509" }}>
                            {row.kills.toLocaleString()}
                          </div>
                          <div className="text-[9px] uppercase text-amber-800/70 font-extrabold tracking-wider">
                            kills
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChannelGrid;
